import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AcademicCapIcon,
  UserGroupIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  StarIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ArrowRightIcon,
  PlayIcon,
  CheckCircleIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import api from "../services/api";
import { getApiUrl } from "../config";
import ChatPopup from "../components/ChatPopup";

interface LandingPageContent {
  sections: any[];
  features: any[];
  testimonials: any[];
  faqs: any[];
  settings: any;
  logos: any[];
  stats: any[];
  socialMedia: any[];
}

const LandingPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState<LandingPageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    fetchLandingPageContent();
  }, []);

  const fetchLandingPageContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Log the API URL being used for debugging
      const apiUrl = getApiUrl();
      console.log('Fetching landing page from:', apiUrl);
      
      const response = await api.get("/landing-page/content");
      setContent(response.data);
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error("Error fetching landing page content:", err);
      setError("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: any } = {
      AcademicCapIcon,
      UserGroupIcon,
      VideoCameraIcon,
      DocumentTextIcon,
      StarIcon,
      EnvelopeIcon,
      PhoneIcon,
      MapPinIcon,
    };
    return iconMap[iconName] || AcademicCapIcon;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">ກຳລັງໂຫຼດ...</p>
        </div>
      </div>
    );
  }

  if (error && !content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            ເກີດຂໍ້ຜິດພາດ
          </h1>
          <p className="text-gray-600 mb-6">
            {error || "ບໍ່ສາມາດໂຫຼດເນື້ອຫາໄດ້"}
          </p>
          <button
            onClick={fetchLandingPageContent}
            className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            ລອງໃໝ່
          </button>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">ກຳລັງໂຫຼດ...</p>
        </div>
      </div>
    );
  }

  const heroSection = content.sections.find((s) => s.section_name === "hero");
  const siteName = content.settings?.site_name || "ເພັດທະບຸນ ສະຫວັດ";
  const contactEmail = content.settings?.contact_email || "info@example.com";
  const contactPhone = content.settings?.contact_phone || "+856 20 XXXX XXXX";
  const contactAddress =
    content.settings?.contact_address || "ນະຄອນຫຼວງວຽງຈັນ, ລາວ";

  // Get logo data
  const navbarLogo = content.logos?.find(
    (logo) => logo.logo_type === "navbar" && logo.is_active
  );
  const footerLogo = content.logos?.find(
    (logo) => logo.logo_type === "footer" && logo.is_active
  );

  // Get stats data
  const activeStats = content.stats?.filter((stat) => stat.is_active) || [];

  // Get social media data
  const activeSocialMedia =
    content.socialMedia?.filter((social) => social.is_active) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
       {/* Navbar */}
            <header className="sticky top-0 bg-white/80 backdrop-blur-md shadow-sm z-50 border-b border-gray-100">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 md:h-20">
                  {/* Logo Group */}
                  <div className="flex items-center min-w-0">
                    {navbarLogo?.logo_url ? (
                      <div className=" flex-shrink-0 transform hover:scale-105 transition-transform duration-200 py-1">
                        <img
                          src={navbarLogo.logo_url}
                          alt={navbarLogo.logo_alt_text || "Logo"}
                          className="rounded-lg h-14 w-auto md:h-16 max-h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-2 shadow-md flex-shrink-0">
                        <AcademicCapIcon className="h-8 w-8 text-white" />
                      </div>
                    )}
                    
                    {/* siteName remains hidden on mobile as requested */}
                    <h2 className="hidden md:block ml-4 text-xl lg:text-2xl font-bold text-gray-900 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent truncate">
                      {siteName}
                    </h2>
                  </div>
      
                  {/* Auth Buttons */}
                  <div className="flex items-center space-x-2 sm:space-x-4 ml-2">
                    <button
                      onClick={() => navigate("/login")}
                      className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-medium px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-base whitespace-nowrap transition-all shadow-md active:scale-95"
                    >
                      ເຂົ້າສູ່ລະບົບ
                    </button>
                    <button
                      onClick={() => navigate("/register")}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-base font-medium transition-all shadow-md transform hover:scale-105 whitespace-nowrap"
                    >
                      ສະໝັກສະມາຊິກ
                    </button>
                  </div>
                </div>
              </div>
            </header>

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 text-white">
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 opacity-95"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
          </div>

          <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-8 sm:py-12 md:py-16 min-h-[40vh] flex items-center relative">
            <div className="max-w-4xl relative z-10 w-full">
              <div className="inline-flex items-center px-4 sm:px-6 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 sm:mb-8 shadow-lg border border-white/20">
                <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 sm:mr-3" />
                <span className="text-xs sm:text-sm font-medium text-white">
                  {heroSection?.content || "ຫຼັກສູດທີ່ດີທີ່ສຸດສຳລັບນັກຮຽນລາວ"}
                </span>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight drop-shadow-lg">
                {heroSection?.title ||
                  "ການຮຽນຮູ້ທັກສະໃນການທັນສະໄຫມຈາກຜູ້ສອນຜູ້ຊຳນານ"}
              </h1>

              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 text-indigo-100 max-w-3xl leading-relaxed">
                {heroSection?.subtitle ||
                  "ຫຼັກສູດອອນລາຍທີ່ອອກແບບສຳລັບນັກຮຽນລາວ ເພື່ອພັດທະນາທັກສະໃນການເຮັດວຽກຈິງ"}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <button
                  onClick={() =>
                    navigate(heroSection?.button_link || "/register")
                  }
                  className="bg-white text-indigo-600 hover:bg-indigo-50 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 flex items-center justify-center group"
                >
                  {heroSection?.button_text || "ເລີ່ມຕົ້ນການຮຽນຮູ້"}
                  <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 ml-2 sm:ml-3 transform group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="border-2 border-white/80 text-white hover:bg-white/10 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 flex items-center justify-center group backdrop-blur-sm"
                >
                  <PlayIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
                  ສຳຫຼວດຫຼັກສູດ
                </button>
              </div>

              {/* Stats */}
              {activeStats.length > 0 ? (
                <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                  {activeStats.map((stat, index) => (
                    <div key={stat.id} className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
                        {stat.stat_value}
                      </div>
                      <div className="text-indigo-200 text-sm sm:text-base">
                        {stat.stat_description}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
                      500+
                    </div>
                    <div className="text-indigo-200 text-sm sm:text-base">
                      ນັກຮຽນທີ່ປະສົບຜົນສຳເລັດ
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
                      50+
                    </div>
                    <div className="text-indigo-200 text-sm sm:text-base">
                      ຫຼັກສູດທີ່ມີຄຸນນະພາບ
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
                      95%
                    </div>
                    <div className="text-indigo-200 text-sm sm:text-base">
                      ຄວາມພໍໃຈຂອງນັກຮຽນ
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-20 right-10 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl"></div>
        </section>

        {/* Features Section */}
        <section className="py-8 sm:py-12 bg-white">
          <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <StarIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                ເປັນຫຍັງຕ້ອງເລືອກພວກເຮົາ?
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                ຄຸນສົມບັດທີ່ເປັນເອກະລັກ
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
                ໂອກາດໃນການພັດທະນາຄວາມສາມາດຂອງທ່ານກັບຫຼັກສູດທີ່ດີທີ່ສຸດຂອງພວກເຮົາ
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {content.features.map((feature, index) => {
                const IconComponent = getIconComponent(feature.icon_name);
                return (
                  <div
                    key={feature.id}
                    className="group bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full transform hover:scale-105 hover:-translate-y-2"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 sm:p-4 rounded-xl sm:rounded-2xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900 group-hover:text-indigo-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base flex-grow leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
                      <button
                        onClick={() =>
                          navigate(feature.link_url || "/register")
                        }
                        className="text-indigo-600 font-semibold flex items-center hover:text-indigo-700 transition-colors group/link text-sm sm:text-base"
                      >
                        {feature.link_text || "ເບິ່ງຫຼັກສູດ"}
                        <ArrowRightIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-2 transform group-hover/link:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-8 sm:py-12 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                ຄຳເຫັນຈາກນັກຮຽນ
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                ສິ່ງທີ່ນັກຮຽນຂອງພວກເຮົາເວົ້າ
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
                ຟັງຈາກນັກຮຽນທີ່ປະສົບຜົນສຳເລັດຂອງພວກເຮົາ ແລະ
                ເບິ່ງວ່າພວກເຂົາມີຄວາມຄິດເຫັນແນວໃດກ່ຽວກັບປະສົບການການຮຽນຂອງພວກເຂົາ
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {content.testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="flex items-center mb-4 sm:mb-6">
                    {testimonial.avatar_url ? (
                      <img
                        src={testimonial.avatar_url}
                        alt={testimonial.name}
                        className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover shadow-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-lg sm:text-xl">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="ml-3 sm:ml-4">
                      <h4 className="font-bold text-gray-900 text-base sm:text-lg">
                        {testimonial.name}
                      </h4>
                      <p className="text-gray-600 text-sm sm:text-base">
                        {testimonial.role}
                      </p>
                      <div className="flex items-center mt-1">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon
                            key={i}
                            className={`h-3 w-3 sm:h-4 sm:w-4 ${
                              i < (testimonial.rating || 5)
                                ? "text-yellow-400 fill-current"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base lg:text-lg leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-8 sm:py-12 bg-white">
          <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <DocumentTextIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                ຄຳຖາມທີ່ພົບເປັນປະຈຳ
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                ຄຳຖາມທີ່ພົບເປັນປະຈຳ
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
                ຄຳຕອບສຳລັດຄຳຖາມທີ່ພົບເປັນປະຈຳກ່ຽວກັບການຮຽນອອນລາຍກັບພວກເຮົາ
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {content.faqs.map((faq, index) => (
                  <div
                    key={faq.id}
                    className="bg-gradient-to-r from-gray-50 to-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold mr-3 sm:mr-4">
                        Q
                      </span>
                      {faq.question}
                    </h3>
                    <p className="text-gray-700 text-sm sm:text-base lg:text-lg leading-relaxed ml-9 sm:ml-12">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-30"></div>
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-6 sm:py-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center mb-4 sm:mb-6">
                {footerLogo?.logo_url ? (
                  <div className="flex-shrink-0">
                    <img
                      src={footerLogo.logo_url}
                      alt={footerLogo.logo_alt_text || "Logo"}
                      className="rounded-lg h-14 w-auto md:h-16 max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl sm:rounded-2xl p-2 sm:p-3 shadow-lg">
                    <AcademicCapIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                  </div>
                )}
                <h3 className="ml-2 sm:ml-3 text-xl sm:text-2xl lg:text-3xl font-bold">
                  {siteName}
                </h3>
              </div>
              <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed mb-4 sm:mb-6">
                ລະບົບການຮຽນອອນລາຍທີ່ທັນສະໄຫມສຳລັບນັກຮຽນລາວ
                ເພື່ອພັດທະນາທັກສະໃນການເຮັດວຽກຈິງ
              </p>

              {/* Social Media */}
              {activeSocialMedia.length > 0 ? (
                <div className="flex space-x-3 sm:space-x-4">
                  {activeSocialMedia.map((social) => (
                    <a
                      key={social.id}
                      href={social.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/10 p-2 sm:p-3 rounded-lg sm:rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
                      title={social.display_name || social.platform_name}
                    >
                      {social.platform_icon ? (
                        <div
                          className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                          dangerouslySetInnerHTML={{
                            __html: social.platform_icon,
                          }}
                        />
                      ) : (
                        <div className="w-4 h-4 sm:w-5 sm:h-5 text-white flex items-center justify-center">
                          <span className="text-xs font-bold">
                            {social.platform_name?.charAt(0) || "S"}
                          </span>
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="flex space-x-3 sm:space-x-4">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 p-2 sm:p-3 rounded-lg sm:rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>

                  <a
                    href="https://line.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 p-2 sm:p-3 rounded-lg sm:rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
                    title="LINE"
                    aria-label="LINE"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                    </svg>
                  </a>

                  <a
                    href="https://wa.me/8562078866695"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 p-2 sm:p-3 rounded-lg sm:rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
                    title="WhatsApp"
                    aria-label="WhatsApp"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                  </a>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
                ຕິດຕໍ່ພວກເຮົາ
              </h4>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="bg-white/10 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                      <EnvelopeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-300" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-xs sm:text-sm text-gray-300">
                        ອີເມວ
                      </h5>
                      <p className="text-white text-sm sm:text-base">
                      {contactEmail}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="bg-white/10 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                      <PhoneIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-300" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-xs sm:text-sm text-gray-300">
                        ເບີໂທ
                      </h5>
                      <p className="text-white text-sm sm:text-base">
                        {contactPhone}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="bg-white/10 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                    <MapPinIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-300" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-xs sm:text-sm text-gray-300">
                      ທີ່ຢູ່
                    </h5>
                    <p className="text-white text-sm sm:text-base">
                      {contactAddress}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/20 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center">
            <p className="text-gray-300 font-medium text-sm sm:text-base">
              &copy; {new Date().getFullYear()} ລະບົບການຮຽນອອນລາຍ
              ສະຫງວນລິຂະສິດທັງໝົດ
            </p>
          </div>
        </div>
      </footer>

      {/* Chat Button */}
      <button
        onClick={() => setIsChatOpen(true)}
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white p-3 sm:p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 z-40"
        title="ສົ່ງຂໍ້ຄວາມເພື່ອສອບຖາມ"
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5 sm:h-6 sm:w-6" />
      </button>

      {/* Chat Popup */}
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
};

export default LandingPage;
